/**
 * @fileoverview Make signature HTML self-contained for email delivery.
 *
 * The rich editor emits bare `<p>` tags. Our editor styles those with its own
 * stylesheet, but a recipient's mail client only sees the HTML we send and
 * applies its own defaults — typically a 1em margin above and below every
 * paragraph. That opens gaps between the lines of a signature (name / role /
 * contact details) that the practitioner never saw while editing.
 *
 * Email HTML has to carry its layout inline, so the stored value is
 * normalised: paragraphs that don't declare a margin get `margin: 0`, and the
 * trailing empty paragraph TipTap leaves behind is dropped.
 */

/** Any margin declaration that already controls the paragraph's spacing. */
const MARGIN_DECLARATIONS = [
  "margin",
  "margin-top",
  "margin-bottom",
  "margin-block",
  "margin-block-start",
  "margin-block-end",
];

function declaresMargin(style: string): boolean {
  return style
    .split(";")
    .map((declaration) => declaration.split(":")[0]?.trim().toLowerCase())
    .some((property) => property && MARGIN_DECLARATIONS.includes(property));
}

/** A paragraph carrying neither text nor embedded content. */
function isEmptyParagraph(paragraph: HTMLElement): boolean {
  if ((paragraph.textContent ?? "").trim().length > 0) return false;
  return paragraph.querySelector("img, table, br") === null;
}

/**
 * Normalise editor output into HTML that renders the same in the settings
 * preview, the composer preview, and the recipient's inbox.
 */
export function normalizeSignatureHtml(html: string): string {
  if (html.trim().length === 0) return "";

  const doc = new DOMParser().parseFromString(html, "text/html");
  const { body } = doc;

  // TipTap keeps a trailing empty paragraph after a pasted block (e.g. a
  // signature table); sent as-is it becomes a blank line under every email.
  while (
    body.lastElementChild instanceof HTMLElement &&
    body.lastElementChild.tagName === "P" &&
    isEmptyParagraph(body.lastElementChild)
  ) {
    body.lastElementChild.remove();
  }

  for (const paragraph of Array.from(body.querySelectorAll("p"))) {
    const style = paragraph.getAttribute("style") ?? "";
    if (declaresMargin(style)) continue;
    paragraph.setAttribute(
      "style",
      style.trim().length > 0 ? `margin: 0; ${style.trim()}` : "margin: 0",
    );
  }

  return body.innerHTML;
}
